import type { Seat } from '@campfire/engine';
import type { ChutesEvent, ChutesView } from '@campfire/game-chutes';
import { serpentine } from '@campfire/track';
import { h } from '../dom.ts';
import { name, type GameUi, type TableCtx } from '../games.ts';
import { seatColor, svg, TOKEN_SLOTS } from './track-common.ts';

const CELL = 60;
const centre = (square: number) => {
  const { row, col } = serpentine(square, 10, 10);
  return { x: col * CELL + CELL / 2, y: row * CELL + CELL / 2 };
};

function board(v: ChutesView): SVGElement {
  const cells: SVGElement[] = [];
  for (let n = 1; n <= 100; n++) {
    const { row, col } = serpentine(n, 10, 10);
    const light = (row + col) % 2 === 0;
    cells.push(
      svg('rect', { x: col * CELL, y: row * CELL, width: CELL, height: CELL, class: `ch-cell ${light ? 'a' : 'b'}${n === 100 ? ' goal' : ''}` }),
      svg('text', { x: col * CELL + 5, y: row * CELL + 15, class: 'ch-num' }, String(n)),
    );
  }
  const links: SVGElement[] = [];
  for (const [f, t] of Object.entries(v.ladders)) {
    const a = centre(Number(f));
    const b = centre(t);
    links.push(svg('g', { class: 'ch-ladder', 'data-ladder': f }, svg('line', { x1: a.x, y1: a.y, x2: b.x, y2: b.y, class: 'rail' }), svg('line', { x1: a.x, y1: a.y, x2: b.x, y2: b.y, class: 'rungs' })));
  }
  for (const [f, t] of Object.entries(v.chutes)) {
    const a = centre(Number(f));
    const b = centre(t);
    const bend = (a.x < 300 ? 1 : -1) * 70;
    links.push(
      svg('g', { class: 'ch-chute', 'data-chute': f },
        svg('path', { d: `M${a.x} ${a.y} Q${(a.x + b.x) / 2 + bend} ${(a.y + b.y) / 2} ${b.x} ${b.y}`, class: 'snake' }),
        svg('circle', { cx: a.x, cy: a.y, r: 9, class: 'head' }),
        svg('circle', { cx: b.x, cy: b.y, r: 5, class: 'tail' })),
    );
  }
  const tokens: SVGElement[] = [];
  const startSlot = new Map<number, number>();
  v.positions.forEach((pos, s) => {
    const slotIdx = TOKEN_SLOTS[(s % TOKEN_SLOTS.length)] as readonly [number, number];
    const c = pos === 0 ? { x: 14 + (startSlot.get(0) ?? 0) * 22, y: 10 * CELL + 18 } : centre(pos);
    if (pos === 0) startSlot.set(0, (startSlot.get(0) ?? 0) + 1);
    const x = pos === 0 ? c.x : c.x + slotIdx[0] * CELL * 0.8;
    const y = pos === 0 ? c.y : c.y + slotIdx[1] * CELL * 0.8;
    tokens.push(
      svg('circle', { cx: x, cy: y, r: 11, fill: seatColor(s), class: `ch-token${s === v.turn ? ' turn' : ''}`, 'data-testid': `token-${s}`, 'data-pos': pos }),
      svg('text', { x, y: y + 4, class: 'ch-tok-n' }, String(s + 1)),
    );
  });
  return svg('svg', { viewBox: `0 0 ${10 * CELL} ${10 * CELL + 36}`, class: 'ch-board', 'data-testid': 'chutes-board', role: 'img', 'aria-label': 'Chutes and Ladders board' },
    ...cells, ...links, svg('text', { x: 6, y: 10 * CELL + 28, class: 'ch-start' }, 'START'), ...tokens);
}

export function chutesEvent(ctx: TableCtx, e: ChutesEvent | null, turn: Seat): string {
  if (!e) return `${name(ctx, turn)} ${turn === ctx.you ? 'go' : 'goes'} first. Spin to start!`;
  const who = name(ctx, e.seat);
  const base = `${who} spun a ${e.spin}`;
  if (e.stayed) return `${base} but needs the exact number to reach 100 — stays on ${e.from}.`;
  if (e.to === 100 && e.jump === null) return `${base} and reached 100! 🎉`;
  if (e.jump === 'ladder') return `${base} and climbed a ladder ${e.landed}→${e.to}!${e.to === 100 ? ' 🎉' : ''}${e.again ? ' Spin again!' : ''}`;
  if (e.jump === 'chute') return `${base} and slid down a chute ${e.landed}→${e.to}.${e.again ? ' Spin again!' : ''}`;
  return `${base}: ${e.from}→${e.to}.${e.again ? ' A six! Spin again!' : ''}`;
}

export const chutesUi: GameUi = {
  id: 'chutes',
  privateHands: false,
  seatInfo: (ctx, s) => {
    const p = (ctx.msg.view as ChutesView).positions[s] ?? 0;
    return p === 0 ? 'at the start' : `square ${p}`;
  },
  render(ctx) {
    const v = ctx.msg.view as ChutesView;
    const over = v.result !== null;
    const canSpin = ctx.myTurn && !over;
    const last = v.last;
    return h(
      'div.chutes-table',
      {},
      h('div.ch-board-wrap', {}, board(v)),
      h(
        'div.ch-side',
        {},
        h('div.ch-spinner', { 'data-testid': 'chutes-result', 'data-spin': last ? last.spin : 0 }, last ? String(last.spin) : '?'),
        h('div.event', { 'data-testid': 'event' }, chutesEvent(ctx, last, v.turn)),
        h(
          'button.primary.ch-spin',
          { 'data-testid': 'chutes-spin', disabled: !canSpin, onclick: () => ctx.act({ type: 'spin' }) },
          over ? 'Game over' : canSpin ? 'Spin!' : `${name(ctx, v.turn)} spins…`,
        ),
        h(
          'div.ch-legend',
          {},
          v.positions.map((p, s) =>
            h(`div.ch-key${s === v.turn && !over ? '.active' : ''}`, { 'data-testid': `key-${s}` }, h('span.dot', { style: `background:${seatColor(s)}` }), `${name(ctx, s)} · ${p === 0 ? 'start' : p}`),
          ),
        ),
        h('div.muted', {}, [v.exactFinish ? 'Exact spin to finish.' : '', v.sixAgain ? 'A six spins again.' : ''].filter(Boolean).join(' ')),
      ),
    );
  },
};
